import { useCallback, useEffect, useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useCourtStateStream } from "@bw/shared/hooks";

import { queryKeys } from "../../../common/query/queryClient";
import { courtBoardQueryOptions } from "./courtBoardQuery";

interface PendingCourtRevision {
  facilityId: string;
  revision: number;
}

interface UseDropInCourtStateOptions {
  enabled?: boolean;
  authorizationExpiresAt?: string | null;
  reconcileBoard?: boolean;
}

export interface DropInCourtStateStatus {
  isActorRevisionPending: boolean;
  isBoardRevisionPending: boolean;
}

/** Reconcile every active player query derived from the facility court cursor. */
export function useDropInCourtState(
  facilityId: string,
  revision: number = 0,
  options: UseDropInCourtStateOptions = {},
): DropInCourtStateStatus {
  const queryClient = useQueryClient();
  const lifecycleActive = useRef(true);
  const requestedEnabled = Boolean(facilityId) && (options.enabled ?? true);
  const authorizationDeadlineConfigured =
    options.authorizationExpiresAt !== undefined;
  const authorizationDeadlineMs =
    typeof options.authorizationExpiresAt === "string"
      ? Date.parse(options.authorizationExpiresAt)
      : Number.NaN;
  const authorizationDeadlineValid =
    !authorizationDeadlineConfigured ||
    Number.isFinite(authorizationDeadlineMs);
  const authorizationExpired =
    authorizationDeadlineConfigured &&
    (!authorizationDeadlineValid || authorizationDeadlineMs <= Date.now());
  const enabled = requestedEnabled && !authorizationExpired;
  const [authorizationSchedule, setAuthorizationSchedule] = useState(0);
  const authorizationRef = useRef({
    requestedEnabled,
    deadlineConfigured: authorizationDeadlineConfigured,
    deadlineMs: authorizationDeadlineMs,
  });
  authorizationRef.current = {
    requestedEnabled,
    deadlineConfigured: authorizationDeadlineConfigured,
    deadlineMs: authorizationDeadlineMs,
  };
  const isAuthorizedNow = useCallback(() => {
    if (!lifecycleActive.current) return false;
    const authorization = authorizationRef.current;
    if (!authorization.requestedEnabled) return false;
    if (!authorization.deadlineConfigured) return true;
    return (
      Number.isFinite(authorization.deadlineMs) &&
      Date.now() < authorization.deadlineMs
    );
  }, []);
  const currentFacilityId = useRef(facilityId);
  currentFacilityId.current = facilityId;
  const discardCourtBoard = useCallback(
    (targetFacilityId: string) => {
      if (!targetFacilityId) return;
      const queryKey = queryKeys.dropin.courtBoard(targetFacilityId);
      void queryClient
        .cancelQueries({ queryKey, exact: true })
        .finally(() => queryClient.removeQueries({ queryKey, exact: true }));
      queryClient.removeQueries({ queryKey, exact: true });
    },
    [queryClient],
  );
  const actorBaseline = useRef({ facilityId, revision });
  if (actorBaseline.current.facilityId !== facilityId) {
    actorBaseline.current = { facilityId, revision };
  }

  const pendingActorRevision = useRef<PendingCourtRevision | null>(null);
  const pendingBoardRevision = useRef<PendingCourtRevision | null>(null);
  const [pendingActorRevisionState, setPendingActorRevisionState] =
    useState<PendingCourtRevision | null>(null);
  const [pendingBoardRevisionState, setPendingBoardRevisionState] =
    useState<PendingCourtRevision | null>(null);

  useEffect(() => {
    lifecycleActive.current = true;
    return () => {
      lifecycleActive.current = false;
    };
  }, []);

  useEffect(() => {
    const scopedFacilityId = facilityId;
    return () => discardCourtBoard(scopedFacilityId);
  }, [discardCourtBoard, facilityId]);

  useEffect(() => {
    if (
      !requestedEnabled ||
      !authorizationDeadlineConfigured ||
      !authorizationDeadlineValid ||
      authorizationDeadlineMs <= Date.now()
    ) {
      return;
    }
    const timer = window.setTimeout(
      () => setAuthorizationSchedule((value) => value + 1),
      Math.min(authorizationDeadlineMs - Date.now(), 2_147_483_647),
    );
    return () => window.clearTimeout(timer);
  }, [
    authorizationDeadlineConfigured,
    authorizationDeadlineMs,
    authorizationDeadlineValid,
    authorizationSchedule,
    requestedEnabled,
  ]);

  const markActorRevisionPending = useCallback(
    (nextRevision: number) => {
      if (!facilityId || currentFacilityId.current !== facilityId) return;
      const current = pendingActorRevision.current;
      const next = {
        facilityId,
        revision:
          current?.facilityId === facilityId
            ? Math.max(current.revision, nextRevision)
            : nextRevision,
      };
      pendingActorRevision.current = next;
      setPendingActorRevisionState(next);
    },
    [facilityId],
  );

  const markBoardRevisionPending = useCallback(
    (nextRevision: number) => {
      if (!facilityId || currentFacilityId.current !== facilityId) return;
      const current = pendingBoardRevision.current;
      const next = {
        facilityId,
        revision:
          current?.facilityId === facilityId
            ? Math.max(current.revision, nextRevision)
            : nextRevision,
      };
      pendingBoardRevision.current = next;
      setPendingBoardRevisionState(next);
    },
    [facilityId],
  );

  const clearActorRevision = useCallback(
    (throughRevision: number) => {
      const pending = pendingActorRevision.current;
      if (
        currentFacilityId.current !== facilityId ||
        pending?.facilityId !== facilityId ||
        pending.revision > throughRevision
      ) {
        return;
      }
      pendingActorRevision.current = null;
      setPendingActorRevisionState((current) =>
        current?.facilityId === facilityId &&
        current.revision <= throughRevision
          ? null
          : current,
      );
    },
    [facilityId],
  );

  const clearBoardRevision = useCallback(
    (throughRevision: number) => {
      const pending = pendingBoardRevision.current;
      if (
        currentFacilityId.current !== facilityId ||
        pending?.facilityId !== facilityId ||
        pending.revision > throughRevision
      ) {
        return;
      }
      pendingBoardRevision.current = null;
      setPendingBoardRevisionState((current) =>
        current?.facilityId === facilityId &&
        current.revision <= throughRevision
          ? null
          : current,
      );
    },
    [facilityId],
  );

  useEffect(() => {
    clearBoardRevision(revision);
  }, [clearBoardRevision, revision]);

  useEffect(() => {
    if (enabled) return;
    pendingActorRevision.current = null;
    pendingBoardRevision.current = null;
    setPendingActorRevisionState(null);
    setPendingBoardRevisionState(null);
    discardCourtBoard(facilityId);
  }, [discardCourtBoard, enabled, facilityId]);

  const reconcileActors = useCallback(
    async (minimumActorRevision?: number) => {
      const requestIsAuthorized = () =>
        currentFacilityId.current === facilityId && isAuthorizedNow();
      if (!requestIsAuthorized()) return true;
      if (minimumActorRevision !== undefined) {
        markActorRevisionPending(minimumActorRevision);
      }
      const keys = [
        queryKeys.dropin.queueStats(facilityId),
        queryKeys.dropin.session(facilityId),
        queryKeys.dropin.paymentOptions(facilityId),
        queryKeys.dropin.checkedInPlayers(facilityId),
        queryKeys.dropin.courts(facilityId),
        queryKeys.dropin.facility(facilityId),
        queryKeys.dropin.myParty(facilityId),
        queryKeys.dropin.partyMemberships(facilityId),
        queryKeys.dropin.myQueueEntry(facilityId),
        ["dropin", "queue-entry"] as const,
        queryKeys.party.all,
      ];
      try {
        await Promise.all(
          keys.map((queryKey) => queryClient.cancelQueries({ queryKey })),
        );
        if (!requestIsAuthorized()) return true;
        await Promise.all(
          keys.map((queryKey) =>
            queryClient.invalidateQueries(
              { queryKey, refetchType: "active" },
              { throwOnError: true },
            ),
          ),
        );
      } catch (error) {
        if (!requestIsAuthorized()) return true;
        throw error;
      }

      if (!requestIsAuthorized()) return true;
      if (currentFacilityId.current !== facilityId) return false;
      if (minimumActorRevision !== undefined) {
        clearActorRevision(minimumActorRevision);
      }
      return true;
    },
    [
      clearActorRevision,
      facilityId,
      isAuthorizedNow,
      markActorRevisionPending,
      queryClient,
    ],
  );

  const reconcileBoard = useCallback(
    async (minimumBoardRevision?: number) => {
      const requestIsAuthorized = () =>
        currentFacilityId.current === facilityId && isAuthorizedNow();
      if (!requestIsAuthorized()) return true;
      if (minimumBoardRevision !== undefined) {
        markBoardRevisionPending(minimumBoardRevision);
      }
      const courtBoardQuery = courtBoardQueryOptions(queryClient, facilityId);
      await queryClient.invalidateQueries({
        queryKey: courtBoardQuery.queryKey,
        exact: true,
        refetchType: "none",
      });
      if (!requestIsAuthorized()) return true;
      let courtBoard;
      try {
        courtBoard = await queryClient.fetchQuery({
          ...courtBoardQuery,
          queryFn: async () => {
            if (!requestIsAuthorized()) {
              throw new Error("Court board authorization expired");
            }
            const response = await courtBoardQuery.queryFn();
            if (!requestIsAuthorized()) {
              throw new Error("Court board authorization expired");
            }
            return response;
          },
          retry: false,
        });
      } catch (error) {
        if (!requestIsAuthorized()) return true;
        throw error;
      }

      if (!requestIsAuthorized()) return true;
      if (currentFacilityId.current !== facilityId) return false;
      if (minimumBoardRevision === undefined) return true;
      const reachedRevision = courtBoard.revision >= minimumBoardRevision;
      if (reachedRevision) {
        clearBoardRevision(minimumBoardRevision);
      }
      return reachedRevision;
    },
    [
      clearBoardRevision,
      facilityId,
      isAuthorizedNow,
      markBoardRevisionPending,
      queryClient,
    ],
  );

  useCourtStateStream({
    facilityId,
    revision,
    onReconcile: reconcileBoard,
    onRevisionPending: markBoardRevisionPending,
    enabled: enabled && (options.reconcileBoard ?? true),
    isAuthorized: isAuthorizedNow,
  });
  useCourtStateStream({
    facilityId,
    revision: actorBaseline.current.revision,
    onReconcile: reconcileActors,
    onRevisionPending: markActorRevisionPending,
    enabled,
    isAuthorized: isAuthorizedNow,
  });

  return {
    isActorRevisionPending:
      enabled && pendingActorRevisionState?.facilityId === facilityId,
    isBoardRevisionPending:
      enabled && pendingBoardRevisionState?.facilityId === facilityId,
  };
}
